/**
 * The project and contract page blocks of wave W1.5 (Mahmoud's walk of 2026-10-10).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * - AttachDocumentsDialog (finding 3b): the page's Attach document control DID NOTHING;
 *   the rail sent it to the page it was already on. It now opens this dialog: several
 *   files, each removable before sending, each attached to the record with an audited
 *   entry in its history. A file that fails stays listed with the reason; the dialog
 *   closes only when every file landed.
 * - DocumentsBlock: what is attached, so the attach has a visible result.
 * - TeamBlock (finding 5b): the CURRENT team, who assigned whom and when, so nobody
 *   assigns blind and nobody wonders whether an assignment replaced someone.
 */
import { LoaderCircle } from "lucide-react";
import { useState } from "react";

import {
  attachToTarget,
  listTargetAttachments,
  type AttachmentTargetEntity,
} from "../api/attachments";
import type { TeamMember } from "../api/portfolio";
import { strings } from "../brand/strings";
import { refusalText } from "../lib/serverErrors";
import { DownloadLink } from "../screens/Inspections";
import { day } from "../screens/parts";
import { useQuery } from "../screens/useQuery";
import { ActionDialog } from "@/components/ui/action-dialog";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { FieldHelper, Refusal } from "@/components/ui/field";
import { FilePicker } from "@/components/ui/file-picker";

const copy = strings.frontDoor;

export interface DocumentTarget {
  readonly entityType: Extract<AttachmentTargetEntity, "PROJECT" | "CONTRACT" | "PARTY">;
  readonly entityId: string;
  /** How the record is named in the dialog and the toast: PRJ-10, CTR-4. */
  readonly label: string;
}

export function AttachDocumentsDialog({
  token,
  target,
  onClose,
  onAttached,
}: {
  readonly token: string;
  readonly target: DocumentTarget;
  readonly onClose: () => void;
  readonly onAttached: (count: number) => void;
}): React.JSX.Element {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attached, setAttached] = useState(0);

  const submit = async (): Promise<void> => {
    setTried(true);
    if (busy || files.length === 0) {
      return;
    }
    setBusy(true);
    setRefusal(null);
    const failed: File[] = [];
    let lastError: unknown = null;
    let landed = attached;
    for (const file of files) {
      try {
        await attachToTarget(token, file, {
          entityType: target.entityType,
          entityId: target.entityId,
          purpose: "GENERAL",
        });
        landed += 1;
      } catch (error: unknown) {
        failed.push(file);
        lastError = error;
      }
    }
    setBusy(false);
    setAttached(landed);
    if (failed.length === 0) {
      onAttached(landed);
      return;
    }
    // What failed stays listed, with the reason, for another try or a removal.
    setFiles(failed);
    setRefusal(
      `${copy.attachDialogFailed(failed.map((one) => one.name))} ${refusalText(lastError)}`,
    );
  };

  const blocked = files.length === 0;
  return (
    <ActionDialog open title={copy.attachDialogHeading} onClose={onClose}>
      <p className="text-base text-muted-foreground">{copy.attachDialogIntro(target.label)}</p>
      <FilePicker
        files={files}
        onChange={(next) => {
          setFiles(next);
          setRefusal(null);
        }}
        label={copy.attachDialogPick}
        testId="attach-files"
        disabled={busy}
      />
      <FieldHelper>{strings.frontDoor.attachHelperNow}</FieldHelper>
      <Refusal message={refusal} />
      <DialogFooter className="justify-between">
        <div className="min-w-0 flex-1 basis-[14rem] text-sm text-muted-foreground">
          {tried && blocked && (
            <span data-testid="attach-blocked">{copy.attachDialogRequired}</span>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {strings.common.cancel}
          </Button>
          <Button
            data-testid="attach-submit"
            disabled={busy}
            aria-disabled={busy || blocked}
            aria-busy={busy}
            onClick={() => {
              void submit();
            }}
          >
            {busy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
            {busy ? copy.attachDialogBusy : copy.attachDialogSubmit(files.length)}
          </Button>
        </div>
      </DialogFooter>
    </ActionDialog>
  );
}

export function DocumentsBlock({
  token,
  target,
  stamp,
}: {
  readonly token: string;
  readonly target: DocumentTarget;
  /** Bumped by the page after an attach, so the list re-reads. */
  readonly stamp: number;
}): React.JSX.Element {
  const documents = useQuery(
    (signal) =>
      listTargetAttachments(
        token,
        { entityType: target.entityType, entityId: target.entityId },
        signal,
      ),
    [token, target.entityType, target.entityId, stamp],
  );
  return (
    <section className="q-section" id="documents" data-testid="documents-block">
      <h3 className="q-section__heading">{copy.documentsHeading}</h3>
      {documents.kind === "loading" && (
        <p className="q-status" role="status">
          {strings.common.loading}
        </p>
      )}
      {(documents.kind === "failed" || documents.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{copy.documentsFailed}</p>
        </div>
      )}
      {documents.kind === "ready" && documents.data.length === 0 && (
        <p className="q-note" data-testid="documents-empty">
          {copy.documentsEmpty}
        </p>
      )}
      {documents.kind === "ready" && documents.data.length > 0 && (
        <ul className="q-history" data-testid="documents-list">
          {documents.data.map((entry) => (
            <li key={entry.linkId} className="q-history__row" data-testid="document-row">
              <DownloadLink attachmentId={entry.attachmentId} filename={entry.filename} />{" "}
              <span className="q-note">
                ({String(Math.max(1, Math.round(entry.fileSizeBytes / 1024)))} kB,{" "}
                {copy.documentsColumnAdded.toLowerCase()} {day(entry.uploadedAt.slice(0, 10))})
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** One member's line: who, in what role, on what, and who put them there. */
export function teamMemberLine(
  member: TeamMember,
  contractLabel: (contractId: string) => string,
): { readonly who: string; readonly scope: string; readonly by: string } {
  const scope =
    member.scope === "CONTRACT" && member.scopeContractId
      ? copy.teamScopeContract(contractLabel(member.scopeContractId))
      : copy.teamScopeProject;
  const by =
    member.assignedByDisplayName === undefined
      ? ""
      : member.assignedByDisplayName === null
        ? copy.teamAssignedBySeed
        : copy.teamAssignedBy(
            member.assignedByDisplayName,
            member.assignedAt ? day(member.assignedAt.slice(0, 10)) : "",
          );
  return { who: `${member.displayName}, ${member.roleName}`, scope, by };
}

export function TeamBlock({
  team,
  contractLabel,
  heading = copy.teamHeading,
  lede = copy.teamLede,
  empty = copy.teamEmpty,
}: {
  readonly team: readonly TeamMember[];
  readonly contractLabel: (contractId: string) => string;
  /** Wave W2: the contract page carries the same block under its own words. */
  readonly heading?: string;
  readonly lede?: string;
  readonly empty?: string;
}): React.JSX.Element {
  return (
    <section className="q-section" id="team" data-testid="team-block">
      <h3 className="q-section__heading">{heading}</h3>
      <p className="q-note">{lede}</p>
      {team.length === 0 ? (
        <p className="q-note" data-testid="team-empty">
          {empty}
        </p>
      ) : (
        <div className="q-register-viewport">
          <table className="q-register" data-testid="team-table">
            <caption>{heading}</caption>
            <thead>
              <tr>
                <th scope="col">{copy.teamColumnPerson}</th>
                <th scope="col">{copy.teamColumnRole}</th>
                <th scope="col">{copy.teamColumnScope}</th>
                <th scope="col">{copy.teamColumnBy}</th>
              </tr>
            </thead>
            <tbody>
              {team.map((member) => {
                const line = teamMemberLine(member, contractLabel);
                return (
                  <tr
                    key={member.id ?? `${member.personId} ${member.roleId}`}
                    data-testid="team-row"
                  >
                    <td>{member.displayName}</td>
                    <td>{member.roleName}</td>
                    <td>{line.scope}</td>
                    <td>{line.by}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
